import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CharacterNetworkDiagram from '../../components/CharacterNetworkDiagram';
import FilterPanel from './components/FilterPanel';
import RelationshipAnalytics from './components/RelationshipAnalytics';
import ThematicGrouping from './components/ThematicGrouping';

import characterService from '../../services/characterService';
import { getAllPlays } from '../../services/playsService';
import openai from '../../lib/openai';

const CharacterNetworkVisualization = () => {
  const { userProfile, isAuthenticated } = useAuth();
  const [plays, setPlays] = useState([]);
  const [selectedPlay, setSelectedPlay] = useState(null);
  const [networkData, setNetworkData] = useState({ nodes: [], links: [] });
  const [filteredData, setFilteredData] = useState({ nodes: [], links: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [relationshipFilter, setRelationshipFilter] = useState('all');
  const [thematicGroups, setThematicGroups] = useState([]);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [selectedAct, setSelectedAct] = useState('all');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchPlays();
  }, []);

  useEffect(() => {
    if (selectedPlay) {
      fetchNetworkData();
    }
  }, [selectedPlay]);

  useEffect(() => {
    applyFilters();
  }, [networkData, relationshipFilter, selectedAct]);

  const fetchPlays = async () => {
    try {
      const playsData = await getAllPlays();
      const publishedPlays = playsData?.filter(p => p?.isPublished);
      setPlays(publishedPlays);
      if (publishedPlays?.length > 0) {
        setSelectedPlay(publishedPlays?.[0]);
      }
    } catch (err) {
      console.error('Error fetching plays:', err);
      setError('Failed to load plays');
    }
  };

  const fetchNetworkData = async () => {
    if (!selectedPlay) return;

    try {
      setIsLoading(true);
      setError(null);
      
      // Get all characters for the play
      const characters = await characterService?.getAllCharacters();
      const playCharacters = characters?.filter(c => c?.playId === selectedPlay?.id);

      if (playCharacters?.length === 0) {
        setNetworkData({ nodes: [], links: [] });
        setFilteredData({ nodes: [], links: [] });
        return;
      }

      // Build comprehensive network from all character relationships
      const allNodes = new Map();
      const allLinks = new Map();

      for (const character of playCharacters) {
        const relationships = await characterService?.getCharacterRelationships(
          selectedPlay?.id,
          character?.name
        );

        // Add nodes
        for (const node of relationships?.nodes || []) {
          if (!allNodes?.has(node?.id)) {
            allNodes?.set(node?.id, {
              ...node,
              lineCount: node?.lineCount || 0,
              imageUrl: playCharacters?.find(c => c?.name === node?.name)?.imageUrl || null
            });
          }
        }

        // Add links
        for (const link of relationships?.links || []) {
          const key = [link?.source, link?.target]?.sort()?.join('-');
          if (!allLinks?.has(key)) {
            allLinks?.set(key, link);
          }
        }
      }

      const nodes = Array.from(allNodes?.values());
      const links = Array.from(allLinks?.values());

      setNetworkData({ nodes, links });
      setFilteredData({ nodes, links });
      
      // Calculate analytics
      calculateAnalytics(nodes, links);
    } catch (err) {
      console.error('Error fetching network data:', err);
      setError(err?.message || 'Failed to load character network');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAnalytics = (nodes, links) => {
    if (!nodes || nodes?.length === 0) return;

    // Network density
    const maxPossibleLinks = (nodes?.length * (nodes?.length - 1)) / 2;
    const density = maxPossibleLinks > 0 ? (links?.length / maxPossibleLinks) * 100 : 0;

    // Character centrality (based on connections)
    const centralityMap = new Map();
    for (const node of nodes) {
      const connections = links?.filter(
        l => l?.source === node?.id || l?.target === node?.id ||
             l?.source?.id === node?.id || l?.target?.id === node?.id
      )?.length;
      centralityMap?.set(node?.id, connections);
    }

    const topCentral = Array.from(centralityMap?.entries())
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 5)
      ?.map(([id, connections]) => ({
        name: nodes?.find(n => n?.id === id)?.name,
        connections
      }));

    // Average connections per character
    const avgConnections = nodes?.length > 0 
      ? (links?.length * 2) / nodes?.length 
      : 0;

    setAnalytics({
      totalCharacters: nodes?.length,
      totalConnections: links?.length,
      networkDensity: density?.toFixed(1),
      averageConnections: avgConnections?.toFixed(1),
      topCentralCharacters: topCentral
    });
  };

  const applyFilters = () => {
    if (!networkData?.nodes || networkData?.nodes?.length === 0) {
      setFilteredData({ nodes: [], links: [] });
      return;
    }

    let filteredNodes = [...networkData?.nodes];
    let filteredLinks = [...networkData?.links];

    // Apply relationship strength filter
    if (relationshipFilter === 'strong') {
      filteredLinks = filteredLinks?.filter(l => l?.value >= 3);
    } else if (relationshipFilter === 'weak') {
      filteredLinks = filteredLinks?.filter(l => l?.value < 3);
    }

    // Filter nodes to only include those with remaining connections
    const connectedNodeIds = new Set();
    for (const link of filteredLinks) {
      const sourceId = typeof link?.source === 'object' ? link?.source?.id : link?.source;
      const targetId = typeof link?.target === 'object' ? link?.target?.id : link?.target;
      connectedNodeIds?.add(sourceId);
      connectedNodeIds?.add(targetId);
    }

    filteredNodes = filteredNodes?.filter(n => connectedNodeIds?.has(n?.id));

    setFilteredData({ nodes: filteredNodes, links: filteredLinks });
    calculateAnalytics(filteredNodes, filteredLinks);
  };

  const handleGenerateInsights = async () => {
    if (!selectedPlay || !networkData?.nodes || networkData?.nodes?.length === 0) return;

    try {
      setIsGeneratingInsights(true);
      setError(null);

      const characterNames = networkData?.nodes?.map(n => n?.name)?.join(', ');
      const connectionCount = networkData?.links?.length;

      const prompt = `Analyze the character network for "${selectedPlay?.title}" by William Shakespeare.

Characters: ${characterNames}
Total Connections: ${connectionCount}

Provide a brief analysis (3-4 sentences) covering:
1. The most central/influential characters based on their connections
2. Key relationship patterns (alliances, conflicts, family bonds)
3. How the network structure reflects major themes in the play

Keep the response concise and insightful.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [{ role: 'user', content: prompt }],
        max_tokens: 300,
        temperature: 0.7
      });

      const insights = response?.choices?.[0]?.message?.content;
      setAiInsights(insights);
    } catch (err) {
      console.error('Error generating insights:', err);
      setError('Failed to generate AI insights. Please check your OpenAI API key.');
    } finally {
      setIsGeneratingInsights(false);
    }
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleExport = () => {
    const exportData = {
      play: selectedPlay?.title,
      generatedAt: new Date()?.toISOString(),
      analytics,
      nodes: filteredData?.nodes,
      links: filteredData?.links,
      aiInsights
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `${selectedPlay?.title?.replace(/\s+/g, '-')}-network-analysis.json`;
    document?.body?.appendChild(a);
    a?.click();
    document?.body?.removeChild(a);
    URL?.revokeObjectURL(url);
  };

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Character Network Visualization', path: '/character-network-visualization' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={userProfile}
        isAuthenticated={isAuthenticated}
        notifications={[]}
      />
      
      <main className="main-content">
        <BreadcrumbTrail items={breadcrumbs} />
        
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Character Network Visualization
          </h1>
          <p className="text-muted-foreground text-lg">
            Explore character relationships, conflict patterns, and thematic connections through interactive network diagrams
          </p>
        </div>

        {/* Play Selection */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[250px]">
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Play
            </label>
            <select
              value={selectedPlay?.id || ''}
              onChange={(e) => {
                const play = plays?.find(p => p?.id === e?.target?.value);
                setSelectedPlay(play);
                setSelectedNode(null);
                setAiInsights(null);
              }}
              className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {plays?.map(play => (
                <option key={play?.id} value={play?.id}>
                  {play?.title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleGenerateInsights}
              loading={isGeneratingInsights}
              disabled={!selectedPlay || isLoading || filteredData?.nodes?.length === 0}
              iconName="Sparkles"
            >
              Generate AI Insights
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              disabled={!selectedPlay || filteredData?.nodes?.length === 0}
              iconName="Download"
            >
              Export Analysis
            </Button>
          </div>
        </div>

        {/* AI Insights */}
        {aiInsights && (
          <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Sparkles" size={20} className="text-primary mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">AI-Powered Insights</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{aiInsights}</p>
              </div>
              <button
                onClick={() => setAiInsights(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[500px]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
              <p className="text-muted-foreground">Loading character network...</p>
            </div>
          </div>
        ) : filteredData?.nodes?.length === 0 ? (
          <div className="flex items-center justify-center min-h-[500px] bg-muted rounded-lg">
            <div className="text-center">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No character data available for this play</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Network Diagram */}
            <div className="lg:col-span-3 space-y-6">
              {/* Filter Controls */}
              <FilterPanel
                relationshipFilter={relationshipFilter}
                onRelationshipFilterChange={setRelationshipFilter}
                selectedAct={selectedAct}
                onActChange={setSelectedAct}
                playId={selectedPlay?.id}
              />

              {/* Network Diagram */}
              <div className="bg-card border border-border rounded-lg p-6">
                <CharacterNetworkDiagram
                  nodes={filteredData?.nodes}
                  links={filteredData?.links}
                  onNodeClick={handleNodeClick}
                />
              </div>

              {/* Thematic Grouping */}
              <ThematicGrouping
                nodes={filteredData?.nodes}
                links={filteredData?.links}
                playTitle={selectedPlay?.title}
              />
            </div>

            {/* Analytics Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              <RelationshipAnalytics
                analytics={analytics}
                selectedNode={selectedNode}
                links={filteredData?.links}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CharacterNetworkVisualization;